import BeeswaxReportProperties from '@/Enums/Report/BeeswaxReportProperties.enum';
import ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import ImpressionByType from '@/components/ImpressionByType';
import { BEESWAX_IMPRESSION_TYPES, WEB_VISITS_BEESWAX_MAP_SCALES } from '@/constants/attribution';
import { getOmnichannelReportingGridData } from '@/helpers/getReportingGridData';
import { isRoleAllowedCpm } from '@/helpers/roles';
import { IRequestSaverFunctions } from '@/hooks/RequestSaver';
import { useAppSelector } from '@/store/hooks';
import { webVisitsReportDispatch } from '@/store/reportingDispatch';
import {
  selectWebVisitsChannelData,
  selectWebVisitsTableReports,
} from '@/store/selectors/attribution/webVisits.selectors';
import { selectWhitelabelTableSettings } from '@/store/selectors/currentWhitelabel.selectors';
import { selectMeRole } from '@/store/selectors/me.selectors';
import { WebVisitsReportType } from '@/types/Attribution/WebVisitsReport.type';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

interface IProps {
  requestSaver: IRequestSaverFunctions;
}

const viewType = ViewType.Attribution;

const ImpressionsByTypeTable = ({ requestSaver }: IProps) => {
  const { channelData } = useAppSelector(selectWebVisitsChannelData);
  const { data, loading } = useAppSelector(selectWebVisitsTableReports);
  const { showLineItemAlternativeId, showCpm, showClientSpend } = useAppSelector(selectWhitelabelTableSettings);
  const meRole = useAppSelector(selectMeRole);

  const isCpmAllowed = isRoleAllowedCpm(meRole);

  const [currentType, setCurrentType] = useState<WebVisitsReportType>(BEESWAX_IMPRESSION_TYPES[0].value);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
  const [printPdfMode, setPrintPdfMode] = useState(false);
  const [checkedForPrintList, setCheckedForPrintList] = useState<string[]>([]);

  const exceptionField = useMemo(
    () => (channelData?.length === 1 ? channelData[0][BeeswaxReportProperties.DIGITAL_CHANNEL] : ''),
    [channelData],
  );

  const handlePrintPdfModeToggle = useCallback(() => {
    if (!printPdfMode) {
      setCheckedForPrintList([currentType]);
      setPaginationModel(prev => ({
        ...prev,
        page: 0,
      }));
    } else {
      setCheckedForPrintList([]);
    }
    setPrintPdfMode(!printPdfMode);
  }, [currentType, printPdfMode, paginationModel]);

  const handleRadioToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked, value: eventValue } = event.target || {};

      if (checked) {
        setCheckedForPrintList([...checkedForPrintList, eventValue]);
        return;
      }
      setCheckedForPrintList(checkedForPrintList.filter(item => item !== eventValue));
    },
    [checkedForPrintList],
  );

  const getReportLoading = useCallback(
    (reportType: WebVisitsReportType) => {
      loading[reportType];
    },
    [loading],
  );

  const getReport = useCallback(
    (reportType: WebVisitsReportType) => {
      return getOmnichannelReportingGridData({
        data,
        reportType,
        exceptionField,
        viewType,
        showCpm: false,
        showClientSpend: isCpmAllowed && showClientSpend,
        showTotalSpend: isCpmAllowed && showCpm,
        hideCtrAndClicks: false,
        showLineItemAlternativeId,
      });
    },
    [data, exceptionField, showLineItemAlternativeId, isCpmAllowed, showClientSpend, showCpm],
  );

  return (
    <ImpressionByType
      impressionTypes={BEESWAX_IMPRESSION_TYPES}
      showViewSwitch={false}
      viewType={viewType}
      viewTypes={[viewType]}
      mapScales={WEB_VISITS_BEESWAX_MAP_SCALES}
      getReportLoading={getReportLoading}
      getReport={getReport}
      requestSaver={requestSaver}
      reportDispatch={webVisitsReportDispatch}
      currentType={currentType}
      setCurrentType={setCurrentType}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      printPdfMode={printPdfMode}
      checkedForPrintList={checkedForPrintList}
      handlePrintPdfModeToggle={handlePrintPdfModeToggle}
      handleRadioToggle={handleRadioToggle}
    />
  );
};

export default ImpressionsByTypeTable;
